import * as commonEnums from '../../../enums/index';
import * as cmxDateTime from '../../date-time/cmx-date-time';
import { isEmpty } from '../../object-utils';

const convertToFilterRecord = (
  categoryKey: string,
  categoryName: string,
  optionsKeyColumn: string,
  optionNameColumn: string,
  filterData: { [key: string]: string }[],
  isSelected: boolean = false,
  displayOrder: number,
  hasNullableOption: boolean = true
): CMxCommonApp.SharedFilter => {
  let convertedData: CMxCommonApp.FilterOption[] = [];
  const uniqueEntries: Set<string> = new Set();
  filterData?.forEach(row => {
    if (
      !row ||
      typeof row !== 'object' ||
      !row.hasOwnProperty(optionsKeyColumn) ||
      !row.hasOwnProperty(optionNameColumn) ||
      (!!!row?.[optionsKeyColumn] && !!!row?.[optionNameColumn])
    )
      return;
    const key = row[optionsKeyColumn] ?? '';
    const name = row[optionNameColumn] ?? '';
    const entryString = `${key}_${name}`;
    if (!uniqueEntries.has(entryString)) {
      convertedData.push({
        categoryKey: categoryKey ?? '',
        categoryName: categoryName ?? '',
        key,
        name,
        defaultSelection: isSelected ? 'INCLUDE' : 'EXCLUDE'
      });
      uniqueEntries.add(entryString);
    }
  });
  if (hasNullableOption) {
    convertedData.splice(0, 0, {
      key: 'noneKey',
      name: 'None',
      categoryKey: categoryKey ?? '',
      categoryName: categoryName ?? '',
      defaultSelection: 'INCLUDE'
    });
  }

  return {
    filterOptions: convertedData,
    type: categoryKey,
    label: categoryName,
    displayOrder: displayOrder || 1
  };
};

const getFilterDateTerms = (
  options: { [key: string]: boolean | Date },
  key: string
): string => {
  let date = options?.[key];
  return date ? cmxDateTime.format(date as Date, cmxDateTime.FORMATS.DATE) : '';
};

const getFilterTerms = (options: {
  [key: string]: boolean | Date;
}): string[] => {
  return Object.entries(options ?? {})
    .filter(([key, value]) => !!key && value)
    .map(([key]) => key);;
};

const updateNoneKeyValue = (includeTerms: string[]): string[] => {
  if (includeTerms.includes('noneKey')) {
    includeTerms.splice(includeTerms.indexOf('noneKey'), 1);
    includeTerms.push('');
  }
  return includeTerms;
};

const getRelatedCasesInitialFilters = (
  filterRawData: {
    [key: string]:
    | CMxCommonApp.NoteTypeRow[]
    | CMxCommonApp.NoteSpecialtyRow[]
    | CMxCommonApp.PatientClassRow[];
  },
  startDate,
  endDate
) => {
  let noteTypeOptions = {};
  filterRawData?.['noteType']?.forEach(entry => {
    if (
      !!entry &&
      entry.hasOwnProperty('Note Description') &&
      !!entry?.['Note Description']
    ) {
      noteTypeOptions[entry?.['Note Description']] = entry.hasOwnProperty(
        'Filter Default'
      )
        ? entry?.['Filter Default'] ===
          commonEnums.DictionaryFilterDefault.INCLUDE
          ? true
          : false
        : true;
    }
  });
  noteTypeOptions['noneKey'] = true;

  let patientClassOptions = {};
  filterRawData?.['patientClass']?.forEach(entry => {
    if (
      !!entry &&
      entry.hasOwnProperty('Patient Class') &&
      !!entry?.['Patient Class']
    ) {
      patientClassOptions[entry?.['Patient Class']] = true;
    }
  });
  patientClassOptions['noneKey'] = true;
  let noteSpecialtyOptions = {};
  filterRawData?.['noteSpecialty']?.forEach(entry => {
    if (
      !!entry &&
      entry.hasOwnProperty('Note Specialty') &&
      !!entry?.['Note Specialty']
    ) {
      noteSpecialtyOptions[entry?.['Note Specialty']] = true;
    }
  });
  noteSpecialtyOptions['noneKey'] = true;
  return {
    'Note Type': noteTypeOptions,
    'Patient Class': patientClassOptions,
    Specialty: noteSpecialtyOptions,
    date_range: { startDate, endDate }
  };
};



const getRelatedCasesFilterRequest = (
  encounterId: string,
  filterSelection: {
    [key: string]: { [key: string]: boolean | Date };
  }
) => {

  const includeNoteTypes = getFilterTerms(
    filterSelection?.['Note Type'] ?? {}
  );
  const hasNoneKeyNoteType = includeNoteTypes.includes('noneKey');

  const includePatientClasses = getFilterTerms(
    filterSelection?.['Patient Class'] ?? {}
  );
  const hasNoneKeyPatientClass = includePatientClasses.includes('noneKey');

  const includeSpecialties = getFilterTerms(
    filterSelection?.['Specialty'] ?? {}
  );
  const hasNoneKeySpecialty = includeSpecialties.includes('noneKey');

  const includeEndDateRange = getFilterDateTerms(
    filterSelection?.['date_range'] ?? {},
    'endDate'
  );
  const includeStartDateRange = getFilterDateTerms(
    filterSelection?.['date_range'] ?? {},
    'startDate'
  );

  const filterClauses = [
    { key: 'description', terms: updateNoneKeyValue(includeNoteTypes), type: 'EQ' },
    { key: 'patient_class', terms: updateNoneKeyValue(includePatientClasses), type: 'IN' },
    { key: 'notes_specialty', terms: updateNoneKeyValue(includeSpecialties), type: 'IN' },
    { key: 'hospital_account_record', terms: [encounterId], type: 'EQ' },
    {
      key: 'date_of_service',
      terms: includeEndDateRange ? [includeEndDateRange] : [],
      type: 'LESS_THAN_EQUAL'
    },
    {
      key: 'date_of_service',
      terms: includeStartDateRange ? [includeStartDateRange] : [],
      type: 'GREATER_THAN_EQUAL'
    },
    ...(hasNoneKeyNoteType ? [{ key: 'description', terms: [], type: 'IS_NULL' }] : []),
    ...(hasNoneKeyPatientClass ? [{ key: 'patient_class', terms: [], type: 'IS_NULL' }] : []),
    ...(hasNoneKeySpecialty ? [{ key: 'notes_specialty', terms: [], type: 'IS_NULL' }] : []),
  ]
    .filter(({ type, terms }) => type === 'IS_NULL' || !isEmpty(terms))
    .map(({ key, terms, type }) => ({ key, terms, type }));
  return filterClauses;
}

export {
  convertToFilterRecord,
  getFilterTerms,
  getRelatedCasesInitialFilters,
  updateNoneKeyValue,
  getFilterDateTerms,
  getRelatedCasesFilterRequest
};
